import { pitchMatchesExpected, type PracticeNoteScore } from "./noteScoring";
import type { PracticePlaybackEvent } from "./practiceEventScoring";

export interface HeardPitchSample {
  tMs: number;
  midi: number;
  cents: number;
}

/** Ventana deslizante de alturas oídas para acordes sin Basic Pitch. */
export const CHORD_HEARD_BUFFER_MS = 320;

export function pruneHeardBuffer(
  buffer: HeardPitchSample[],
  nowMs: number,
  maxAgeMs = CHORD_HEARD_BUFFER_MS
): void {
  const cutoff = nowMs - maxAgeMs;
  while (buffer.length > 0 && buffer[0].tMs < cutoff) {
    buffer.shift();
  }
}

export function recordHeardPitch(
  buffer: HeardPitchSample[],
  sample: HeardPitchSample,
  maxAgeMs = CHORD_HEARD_BUFFER_MS
): void {
  buffer.push(sample);
  pruneHeardBuffer(buffer, sample.tMs, maxAgeMs);
}

export type PitchMatchFn = (heardMidi: number, expectedMidi: number, cents: number) => boolean;

/**
 * Marca voces pendientes del evento si su altura apareció en el buffer reciente.
 * Rápido (solo YIN/mono), adecuado para practicar sin bloquear el playhead.
 */
export function matchPendingNoteIndicesFromHeard(
  event: PracticePlaybackEvent,
  scores: PracticeNoteScore[],
  buffer: HeardPitchSample[],
  songMs: number,
  pitchMatch: PitchMatchFn = (h, e, c) =>
    pitchMatchesExpected(h, e, c, true, 54)
): number[] {
  const matched: number[] = [];
  for (const idx of event.noteIndices) {
    const score = scores.find((s) => s.noteIndex === idx);
    if (!score || score.status !== "pending") continue;
    if (songMs < score.startMs - score.earlyMs) continue;
    if (songMs > score.endMs + score.lateMs + score.lateGraceMs + score.sustainTailMs) continue;

    const expected = score.expectedMidi;
    const heard = buffer.some((s) => pitchMatch(s.midi, expected, s.cents));
    if (heard) matched.push(idx);
  }
  return matched;
}
